// SkipGate — Gate G3.7 Phase 0.
//
// Source: ``plan/AI_ALM_Gate_G3_7_Implementation_Plan.md`` §2.2.3.
//
// A small panel rendered at the top of every *optional* gate
// surface (G2, G2.5, G3, G3.5, and the future G3.7) that gives the
// architect an explicit "Skip with reason …" affordance. Without
// it, the architect at minimal/standard budget who wants to
// elide a gate has to discover the workflow by trial and error.
//
// The panel is intentionally lightweight — a single warning row
// plus a "Skip with reason …" button. Clicking the button pops
// a confirm dialog that requires a non-empty justification (≤500
// chars per the plan §2.3 model contract). Submission flips the
// gate's decision(s) to ``status: skipped`` with the architect's
// reason recorded on every decision; the Phase 0 backend then
// treats the gate as resolved per ``gate_enforcer.assert_gates_clear``.
//
// SkipGate does NOT render for mandatory gates (G1, G4, G5) — the
// optionality matrix in :mod:`gateOptionality` controls visibility
// here.

import { useState } from 'react'
import { AlertTriangle, SkipForward, X } from 'lucide-react'

import type { FocusKey } from './focusKeys'
import { isOptionalGate, MAX_SKIP_REASON_LENGTH } from './gateOptionality'

// --------------------------------------------------------------------------- //
// Types
// --------------------------------------------------------------------------- //
interface SkipGateProps {
  /** Which optional gate the panel is wrapping. */
  focusKey: FocusKey
  /**
   * Async callback fired when the architect submits the dialog. The
   * caller is responsible for translating the reason into a backend
   * decision-update mutation (one call per pending decision flipped
   * to ``status: skipped``).
   */
  onSkip: (reason: string) => Promise<void> | void
  /**
   * If true, the panel is hidden — caller-side override for cases
   * where the gate is already in a non-skippable state (e.g. the
   * architect already approved one decision; UI logic decides).
   */
  hidden?: boolean
}

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //
export function SkipGate({ focusKey, onSkip, hidden }: SkipGateProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  if (hidden) return null
  if (!isOptionalGate(focusKey)) return null

  return (
    <>
      <div className="alm-skipgate-panel mb-4 rounded-lg border border-amber-700/40 bg-amber-950/20 p-3 text-sm text-amber-100">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold">
              This gate is optional at your current intervention budget.
            </p>
            <p className="text-amber-200/80 mt-0.5 leading-snug">
              You can skip it by recording a reason, or work through the
              decisions below. Skipping is logged on the audit trail.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="alm-skipgate-btn shrink-0 inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/20"
          >
            <SkipForward className="h-3.5 w-3.5" /> Skip with reason …
          </button>
        </div>
      </div>

      {dialogOpen && (
        <SkipReasonDialog
          focusKey={focusKey}
          onClose={() => setDialogOpen(false)}
          onSubmit={async (reason) => {
            await onSkip(reason)
            setDialogOpen(false)
          }}
        />
      )}
    </>
  )
}

// --------------------------------------------------------------------------- //
// Dialog
// --------------------------------------------------------------------------- //
function SkipReasonDialog({
  focusKey,
  onClose,
  onSubmit,
}: {
  focusKey: FocusKey
  onClose: () => void
  onSubmit: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = reason.trim()
  const isEmpty = trimmed.length === 0
  const isTooLong = trimmed.length > MAX_SKIP_REASON_LENGTH
  const canSubmit = !isEmpty && !isTooLong && !submitting

  return (
    <div
      className="alm-skipgate-dialog-shell fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="skipgate-dialog-title"
    >
      <div className="alm-skipgate-dialog w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3
              id="skipgate-dialog-title"
              className="text-base font-bold text-slate-100"
            >
              Skip {focusKeyToLabel(focusKey)}?
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Tell us why you're skipping this gate. The reason is
              recorded on the audit trail and can be reviewed later.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
          Reason (required)
        </label>
        <textarea
          className="w-full min-h-[96px] rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="e.g. Out of v1 scope, tracked in JIRA-1234."
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            setError(null)
          }}
          maxLength={MAX_SKIP_REASON_LENGTH + 50}
          autoFocus
        />
        <div className="flex items-center justify-between mt-1 text-[11px]">
          <span
            className={`font-mono tabular-nums ${
              isTooLong ? 'text-rose-300' : 'text-slate-500'
            }`}
          >
            {trimmed.length}/{MAX_SKIP_REASON_LENGTH}
          </span>
          {isEmpty && (
            <span className="text-amber-300">Reason cannot be empty.</span>
          )}
          {isTooLong && (
            <span className="text-rose-300">
              Trim to {MAX_SKIP_REASON_LENGTH} characters or fewer.
            </span>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-rose-700/40 bg-rose-950/40 p-2 text-xs text-rose-200">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!canSubmit) return
              setSubmitting(true)
              setError(null)
              try {
                await onSubmit(trimmed)
              } catch (e) {
                setError(
                  e instanceof Error ? e.message : 'Failed to record skip',
                )
                setSubmitting(false)
              }
            }}
            disabled={!canSubmit}
            className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
          >
            {submitting ? 'Recording…' : 'Skip with reason'}
          </button>
        </div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Helpers
// --------------------------------------------------------------------------- //
function focusKeyToLabel(focusKey: FocusKey): string {
  switch (focusKey) {
    case 'gate2':
      return 'Gate G2 (Constraint review)'
    case 'gate25':
      return 'Gate G2.5 (Compliance Triage)'
    case 'gate3':
      return 'Gate G3 (Swarm sanity)'
    case 'gate35':
      return 'Gate G3.5 (Coherence)'
    case 'gate37':
      return 'Gate G3.7 (Technical Depth Audit)'
    default:
      return 'this gate'

  }
}
